import { FC, Fragment, useMemo, MouseEvent, useCallback, useEffect, useRef, useState, SyntheticEvent } from 'react'
import { Box, Typography, IconButton, Tab } from '@mui/material'
import { useAuth } from '@frontegg/react'
import { hasAnyRole, Role } from 'lib/utils'
import theme from 'theme'

/* Utils */
import {
  nistPillars,
  groupSSMData,
  getNistPillarBackground,
  getHeaderItems,
  getBudgetBreakpoints
} from './securityStackMap.utils'
import { useAppDispatch } from '../../../store/hooks'
import { notification, closeNotification as closeToast } from '../../../store/notifications/notificationSlice'
import {
  SSMAssetType,
  SSMGroupedData,
  SSMNistPillar,
  SSMResponseProduct,
  SSMResponseV2,
  SSMGroupedDataItem,
  SSMResponseProductExtended,
  VendorExtended,
  SSMCell,
  SSMCellProduct
} from '../../../models'
import axios from '../../../lib/axios'

/* Components */
import {
  Container,
  Header,
  ContentContainer,
  SSMGrid,
  GridHeader,
  CornerCell,
  EmptySSMGridContainer,
  ControlsWrapper,
  ControlsContainer
} from './ssm.styles'
import SSMCard from './components/SSMCard'
import BudgetViewCard from './components/BudgetViewCard'
import ViewToggle from '../dashboard/components/OverallPerformanceScore/ViewToggle'
import { FullScreenIcon, NistIcon } from '../../components/svg'
import NistPillar from './components/NistPillar'
import ConfirmRemovalDialog from './components/ConfirmRemovalDialog'
import { IntegrationsTabs, IntegrationsTabsWrapper } from '../settings/integrations/integrations.styles'
import { a11yProps } from '../cpi-details/components/LibraryDetailsTabPanel'
import { LibraryDetailsHoverTooltip } from '../../components/tooltip'
import ControlButton from '../cpi-details/sections/devices/components/ControlButton'
import TrendsChartSelectField from '../cpi-details/sections/trends-tab/components/TrendsChartSelectField'
import SSMDrawer from './ssm-drawer/SSMDrawer'
import SSMHistoricalOverview from './historical-overview/SSMHistoricalOverview'
import { useQuery } from 'react-query'
import { downloadFile } from '../reports/reports.utils'

const year = new Date().getFullYear()
export const contractYearOptions = ['Active Now', `${year - 2}`, `${year - 1}`, `${year}`, `${year + 1}`, `${year + 2}`]

export interface CurrentCard {
  open: boolean
  nist?: SSMNistPillar
  asset?: SSMAssetType
  target?: HTMLDivElement | null
  vendor?: VendorExtended
}

const placeholderAssets: SSMGroupedDataItem[] = [
  {
    nist: SSMNistPillar.Identify,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Identify,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Identify,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Identify,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Identify,
    asset: SSMAssetType.Users,
    items: []
  },
  {
    nist: SSMNistPillar.Protect,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Protect,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Protect,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Protect,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Protect,
    asset: SSMAssetType.Users,
    items: []
  },
  {
    nist: SSMNistPillar.Detect,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Detect,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Detect,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Detect,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Detect,
    asset: SSMAssetType.Users,
    items: []
  },
  {
    nist: SSMNistPillar.Respond,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Respond,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Respond,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Respond,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Respond,
    asset: SSMAssetType.Users,
    items: []
  },
  {
    nist: SSMNistPillar.Recover,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Recover,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Recover,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Recover,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Recover,
    asset: SSMAssetType.Users,
    items: []
  },
  {
    nist: SSMNistPillar.Govern,
    asset: SSMAssetType.Devices,
    items: []
  },
  {
    nist: SSMNistPillar.Govern,
    asset: SSMAssetType.Applications,
    items: []
  },
  {
    nist: SSMNistPillar.Govern,
    asset: SSMAssetType.Networks,
    items: []
  },
  {
    nist: SSMNistPillar.Govern,
    asset: SSMAssetType.Data,
    items: []
  },
  {
    nist: SSMNistPillar.Govern,
    asset: SSMAssetType.Users,
    items: []
  }
]
const headers = ['Devices', 'Applications', 'Networks', 'Data', 'Users']
export const SSM_EMPTY_BANNER_CLOSED = 'ssm_empty_banner_closed'

type ViewType = 'current' | 'trend'

export interface ConfirmRemoveProps {
  product?: SSMCellProduct
  cell?: SSMCell
  open: boolean
  numOfTiles?: number
  type: 'local' | 'global'
  affectedCells?: Array<{ nist: string; asset: string }>
}

export interface HeaderItemProps {
  headerCost: number
  numOfItems: number
  totalProducts: number
}

export interface DrawerProps {
  nist: SSMNistPillar | null
  asset: SSMAssetType | null
  assets: SSMResponseProduct[]
}

const SSM: FC = () => {
  const dispatch = useAppDispatch()
  const gridContainerRef = useRef<HTMLDivElement>(null)
  const { user: authUser } = useAuth()
  const [view, setView] = useState('coverage')
  const [loading, setLoading] = useState(true)
  const [groupedData, setGroupedData] = useState<SSMGroupedData>([])
  const [allProducts, setAllProducts] = useState<SSMResponseProduct[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerProps, setDrawerProps] = useState<DrawerProps>({ nist: null, asset: null, assets: [] })
  const [budgetBreakpoints, setBudgetBreakpoints] = useState([0, 0, 0, 0, 0])
  const [confirmRemoveProps, setConfirmRemoveProps] = useState<ConfirmRemoveProps>({
    open: false,
    numOfTiles: 0,
    type: 'local',
    affectedCells: []
  })
  const [headerItems, setHeaderItems] = useState<Record<string, HeaderItemProps | number>>({})
  const [removing, setRemoving] = useState(false)
  const [emptyStateBanner, setEmptyStateBanner] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const ssmEmptyBannerClosed =
    window && typeof window !== 'undefined' ? localStorage.getItem(SSM_EMPTY_BANNER_CLOSED) : 'false'
  const [contractYear, setContractYear] = useState<string>(contractYearOptions[0])
  const [activeView, setActiveView] = useState<ViewType>('current')
  const [cell, setCell] = useState<SSMCell | null>(null)
  const [fetchingCell, setFetchingCell] = useState(false)
  const [generatingSSMReport, setGeneratingSSMReport] = useState(false)
  const [noActiveCpis, setNoActiveCpis] = useState(false)
  const [ssmReportCode, setSsmReportCode] = useState('')
  const closeNotification = useRef<any>(null)

  const ssmReportQuery = useQuery(
    ['ssmReportCode', { ssmReportCode }],
    () => axios.get(`/api/v1/reports/download_url/${ssmReportCode}`),
    {
      refetchInterval: 5000,
      enabled: !!ssmReportCode
    }
  )

  useEffect(() => {
    if (ssmReportQuery.data && !ssmReportQuery.error && !ssmReportQuery.isFetching) {
      const downloadUrl = ssmReportQuery.data.data.reportDownloadUrl.url
      downloadFile(`${downloadUrl}`, 'SSM Report')
      dispatch(
        notification({
          open: true,
          variant: 'success',
          message: 'SSM Report PDF Successfully Downloaded'
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      setSsmReportCode('')
    }
  }, [ssmReportQuery.data, ssmReportQuery.error, ssmReportQuery.isFetching])

  const getMaxCellValue = (data: any) => {
    if (data && data.length > 0) {
      let maxCellValue = 0

      const arr = [...nistPillars]
      arr.forEach((nist) => {
        const assets: SSMGroupedDataItem[] = data.filter((n: any) => n.nist === nist)

        assets.forEach(({ items }) => {
          const cellTotalCost = items.length > 0 ? items.reduce((acc, obj) => acc + obj.cost, 0) : 0

          if (cellTotalCost > maxCellValue) {
            maxCellValue = cellTotalCost
          }
        })
      })

      return maxCellValue
    }

    return 0
  }

  const handleRemoveCompletely = async (product: SSMCellProduct) => {
    if (!cell || !product) return

    setRemoving(true)

    const groupArr: any[] = []
    groupedData.forEach((group) => {
      const filteredItems = group.items.filter(
        (item) => (item as any).uuid !== product.uuid && (item as any).uuid !== product.uuid
      )

      groupArr.push({
        ...group,
        items: filteredItems
      })
    })
    setGroupedData(groupArr)
    dispatch(
      notification({
        open: true,
        variant: 'success',
        message: `${product.display_name} was successfully removed from the Security Stack Map`
      })
    )
    const newAssets = allProducts.filter((n) => n.display_name !== product.display_name)
    updateDrawerProps(cell.nist as SSMNistPillar, cell.asset as SSMAssetType, newAssets)
    const oldRemoveProps = { ...confirmRemoveProps }
    setConfirmRemoveProps({ ...oldRemoveProps, open: false })

    try {
      let url = '/api/v3/ssm'
      if (contractYear !== 'Active Now') {
        url += '?year=' + contractYear
      }
      const res = await axios.delete(url, {
        data: {
          itemId: product.uuid
        }
      })

      if (res.status.toString().includes('20')) {
        if (drawerProps && drawerProps.nist && drawerProps.asset) {
          await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
        }
        await fetchAndGroupData(contractYear)
      }
    } catch (err) {
      console.error(err)
      await fetchAndGroupData()
      dispatch(
        notification({
          open: true,
          variant: 'error',
          message: `Failed removing product. Try again later`
        })
      )
    } finally {
      setRemoving(false)
    }
  }

  const handleRemoveFromCell = async (product: SSMCellProduct) => {
    if (!cell || !product) return

    setRemoving(true)
    const updatedGroupedData = groupedData.map((group) => {
      if (group.nist === cell.nist && group.asset === cell.asset) {
        const updatedItems = group.items.filter((n: any) => n.display_name !== product.display_name)
        updateDrawerProps(cell.nist as SSMNistPillar, cell.asset as SSMAssetType, updatedItems as any)
        return {
          ...group,
          items: updatedItems
        }
      }
      return group
    })
    const currentGridItems = updatedGroupedData.map((n) => n.items.map((c) => c)).flat()
    if (!currentGridItems.length) {
      setEmptyStateBanner(true)
    }
    setGroupedData(updatedGroupedData)
    dispatch(
      notification({
        open: true,
        variant: 'success',
        message: `${product.display_name} was successfully removed from the cell`
      })
    )
    setConfirmRemoveProps({
      open: false,
      type: 'local'
    })

    try {
      let url = '/api/v3/ssm'
      if (contractYear !== 'Active Now') {
        url += '?year=' + contractYear
      }
      const res = await axios.delete(url, {
        data: {
          itemId: product.uuid,
          nist: cell.nist.toLowerCase() || '',
          asset: cell.asset.toLowerCase() || ''
        }
      })

      if (res.status.toString().includes('20')) {
        if (drawerProps && drawerProps.nist && drawerProps.asset) {
          await fetchCellDetails(drawerProps.nist.toLowerCase(), drawerProps.asset.toLowerCase())
        }
        await fetchAndGroupData(contractYear)
      }
    } catch (err) {
      console.error(err)
      await fetchAndGroupData()
      dispatch(
        notification({
          open: true,
          variant: 'error',
          message: `Failed to remove ${product.display_name} from the Security Stack Map`
        })
      )
    } finally {
      setRemoving(false)
    }
  }

  const handleCloseConfirmRemoveDialog = () => {
    setConfirmRemoveProps((pre: any) => ({ ...pre, open: false }))
  }

  const fetchAndGroupData = useCallback(async (year?: string) => {
    let url = '/api/v3/ssm'
    if (year && year !== 'Active Now') {
      url += '?year=' + year
    }

    const res = await axios.get<SSMResponseV2>(url)

    if (res.status === 200) {
      const { data, products } = res.data

      if (!data.length) {
        return
      }

      const groupedData = groupSSMData(data, products)
      const headerItems = getHeaderItems(groupedData)
      const maxCellVal = getMaxCellValue(groupedData)
      const budgetBreakpoints = getBudgetBreakpoints(maxCellVal)
      setBudgetBreakpoints(budgetBreakpoints)
      setHeaderItems(headerItems)
      setGroupedData(groupedData)
      setAllProducts(products)

      return products
    }
  }, [])

  const fetchSSMData = useCallback(async () => {
    setLoading(true)

    try {
      await fetchAndGroupData()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchCellDetails = useCallback(
    async (nist: string, asset: string) => {
      setFetchingCell(true)
      try {
        let url = `/api/v3/ssm/cell?nist=${nist}&asset=${asset}`
        if (contractYear !== 'Active Now') {
          url += `&year=${contractYear}`
        }
        const res = await axios.get(url)
        setCell(res.data)
      } catch (err) {
        console.error(err)
      } finally {
        setFetchingCell(false)
      }
    },
    [contractYear]
  )

  useEffect(() => {
    fetchSSMData().catch((e) => e)
  }, [])

  useEffect(() => {
    if (allProducts.length > 0 && !loading) {
      setEmptyStateBanner(false)
    }
  }, [loading, allProducts, ssmEmptyBannerClosed])

  const fetchAllCpis = async () => {
    const res = await axios.get('/api/v3/cpis')

    if (res.status === 200) {
      const data = res.data.data
      const hasConfig = data.cpis.some((c: any) => c.configuration)

      if (!hasConfig) {
        setNoActiveCpis(true)
      }
    }
  }

  useEffect(() => {
    fetchAllCpis().catch((e) => e)
  }, [])

  const handleChangeView = (event: MouseEvent<HTMLElement>, newView: string) => {
    if (loading) {
      event.preventDefault()
    } else {
      setView(newView ?? view)
    }
  }

  const handleOpenDrawer = (nist: SSMNistPillar, asset: SSMAssetType, assets: SSMResponseProduct[]) => {
    setDrawerOpen(true)
    setDrawerProps({ nist, asset, assets })
    fetchCellDetails(nist.toLowerCase(), asset.toLowerCase())
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setDrawerProps({ nist: null, asset: null, assets: [] })
  }

  const handleFullScreen = () => {
    if (document.fullscreenElement !== undefined && document.fullscreenElement === null) {
      document.body.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  const updateDrawerProps = (nist: SSMNistPillar, asset: SSMAssetType, assets: SSMResponseProduct[]) => {
    setDrawerProps({ nist, asset, assets })
  }

  const canEdit = useMemo(() => hasAnyRole([Role.Owner, Role.Admin, Role.Demo], authUser), [authUser])

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleViewChange = (event: MouseEvent<HTMLElement>, newAlignment: string) => {
    setActiveView(newAlignment as ViewType)
    handleChangeView(event, newAlignment === 'current' ? 'coverage' : 'budget')
  }

  const generateSSMReport = async () => {
    if (noActiveCpis) {
      dispatch(
        notification({
          open: true,
          variant: 'warning',
          message: 'Report Generation Error: No active CPIs were found. Please activate CPIs to generate a report.'
        })
      )

      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)

      return
    }

    setGeneratingSSMReport(true)

    try {
      const res = await axios.post(`/api/v1/reports/ssm`)

      if (res.status.toString().startsWith('2')) {
        const { code } = res.data
        setSsmReportCode(code)
      }
    } catch (e) {
      dispatch(
        notification({
          open: true,
          variant: 'error',
          message: 'Failed to Export Report. Please Contact Our Support Team'
        })
      )
    } finally {
      setGeneratingSSMReport(false)
    }
  }

  return (
    <>
      <Container className="ssm-container">
        <Header className="header">
          <Box className="header-top">
            <Typography className="header-title">Security Stack Map (SSM)</Typography>
            <Box className="header-right">
              <div>
                <LibraryDetailsHoverTooltip
                  title={ssmReportQuery.isFetching || generatingSSMReport ? 'Generating...' : 'Export SSM Report'}
                >
                  <div>
                    <ControlButton
                      type="export-pdf"
                      loading={generatingSSMReport || ssmReportQuery.isFetching}
                      handleClick={generateSSMReport}
                    />
                  </div>
                </LibraryDetailsHoverTooltip>
              </div>
              <div>
                <LibraryDetailsHoverTooltip title="Full Screen">
                  <IconButton onClick={handleFullScreen}>
                    <FullScreenIcon />
                  </IconButton>
                </LibraryDetailsHoverTooltip>
              </div>
            </Box>
          </Box>
          <IntegrationsTabsWrapper sx={{ width: '100%' }}>
            <IntegrationsTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="ssm-tabs"
              TabIndicatorProps={{
                style: { display: 'none' }
              }}
              isdetails="true"
            >
              <Tab label="Security Stack Map" {...a11yProps(0)} />
              <Tab label="Historical Overview" {...a11yProps(1)} />
            </IntegrationsTabs>
          </IntegrationsTabsWrapper>
        </Header>

        {tabValue === 0 && (
          <>
            <ControlsContainer>
              <ControlsWrapper className="controls-wrapper">
                <ViewToggle
                  ssm
                  activeView={activeView}
                  onViewChange={handleViewChange}
                  paperSx={{
                    width: 'fit-content',

                    '& .MuiToggleButtonGroup-root': {
                      '& .MuiToggleButton-root': {
                        gap: '8px !important',
                        width: 'fit-content !important',
                        paddingLeft: '16px !important',
                        paddingRight: '24px !important'
                      }
                    }
                  }}
                />
                <TrendsChartSelectField
                  options={contractYearOptions}
                  label=""
                  placeholder="Filter by Time Period"
                  labeltext="Filter by: "
                  value={contractYear}
                  handleChange={(e: any) => {
                    const value = e.target.value
                    setContractYear(value)
                    fetchAndGroupData(value)
                  }}
                  disabled={false}
                  minWidth="144px !important"
                  hideSelected
                />
              </ControlsWrapper>

              <Box className="total-products-wrapper">
                <Typography className="total-products">Total: </Typography>
                <Typography className="total-products-count">
                  {allProducts.length} <span>product{allProducts.length > 1 && 's'}</span>
                </Typography>
              </Box>
            </ControlsContainer>

            <ContentContainer ref={gridContainerRef} className="grid-container">
              <EmptySSMGridContainer opacity={emptyStateBanner ? 1 : 0}>
                <Box className="empty-message-wrapper">
                  <Box className="empty-message-inner">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                        fill={theme.colors.white}
                      />
                    </svg>
                    <Typography className="empty-message-text">
                      Your Security Stack Map is empty. Hover over a cell to start adding products.
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => {
                      setEmptyStateBanner(false)
                      localStorage.setItem(SSM_EMPTY_BANNER_CLOSED, 'true')
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <g clipPath="url(#clip0_182_72997)">
                        <path
                          d="M14 -0.388572L12.3886 -2L6 4.38857L-0.388572 -2L-2 -0.388572L4.38857 6L-2 12.3886L-0.388572 14L6 7.61143L12.3886 14L14 12.3886L7.61143 6L14 -0.388572Z"
                          fill={theme.colors.white}
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_182_72997">
                          <rect width="12" height="12" fill={theme.colors.white} />
                        </clipPath>
                      </defs>
                    </svg>
                  </IconButton>
                </Box>
              </EmptySSMGridContainer>

              <SSMGrid className="grid">
                <CornerCell>
                  <NistIcon />
                </CornerCell>
                {['header', ...nistPillars].map((nist, nistIndex: number) => {
                  const assets: SSMGroupedDataItem[] =
                    groupedData.length > 0
                      ? groupedData.filter((n) => n.nist === nist)
                      : placeholderAssets.filter((n) => n.nist === nist)

                  let budgetRowCost = 0
                  let totalRowProducts = 0
                  assets.forEach(({ items }: any) => {
                    totalRowProducts += items.length
                    if (items.length > 0) {
                      const cellTotalCost =
                        items.length > 0 ? items.reduce((acc: any, obj: any) => acc + obj.cost, 0) : 0
                      budgetRowCost += cellTotalCost
                    }
                  })
                  const budgetRowCostFormatted = Intl.NumberFormat('en-US', {
                    notation: 'compact',
                    maximumFractionDigits: 2
                  }).format(budgetRowCost)

                  if (nist === 'header') {
                    return headers.map((header, headerIdx) => {
                      const selector = header.toLowerCase()
                      const headerProps = headerItems[selector]
                      if (headerProps && typeof headerProps !== 'number') {
                        const { totalProducts, headerCost } = headerProps
                        const totalCost = Intl.NumberFormat('en-US', {
                          notation: 'compact',
                          maximumFractionDigits: 2
                        }).format(headerCost)

                        return (
                          <GridHeader key={headerIdx}>
                            <Typography className="name">{header}</Typography>
                            {view === 'coverage' ? (
                              <Box className="header-value">
                                <span className="header-value-count">{totalProducts}</span>
                                <span className="header-value-label">
                                  {totalProducts === 1 ? 'product' : 'products'}
                                </span>
                                <span className="header-value-label-short">
                                  {totalProducts === 1 ? 'prod' : 'prods'}
                                </span>
                              </Box>
                            ) : (
                              <Box className="header-value">
                                <span className="header-value-count">${totalCost}</span>
                              </Box>
                            )}
                          </GridHeader>
                        )
                      }

                      return (
                        <GridHeader key={headerIdx}>
                          <Typography className="name">{header}</Typography>
                          <Box className="header-value">
                            <span className="header-value-count">{view === 'coverage' ? '0' : '0'}</span>
                            {view === 'coverage' && (
                              <>
                                <span className="header-value-label">products</span>
                                <span className="header-value-label-short">prods</span>
                              </>
                            )}
                          </Box>
                        </GridHeader>
                      )
                    })
                  }
                  return (
                    <Fragment key={nistIndex}>
                      <NistPillar
                        nistPillarText={nist}
                        productCount={view === 'coverage' ? totalRowProducts : undefined}
                        nistValueText={view !== 'coverage' ? `$${budgetRowCostFormatted}` : undefined}
                        background={getNistPillarBackground(nist as SSMNistPillar)}
                      />
                      <>
                        {view === 'coverage' ? (
                          <>
                            {assets.map(({ asset, items }: any, assetIndex) => {
                              return (
                                <SSMCard
                                  key={`${asset}-${assetIndex}`}
                                  asset={asset as SSMAssetType}
                                  nist={nist as SSMNistPillar}
                                  items={items as unknown as SSMResponseProduct[]}
                                  handleOpenDrawer={handleOpenDrawer}
                                  handleCloseDrawer={handleCloseDrawer}
                                  loading={loading}
                                  canEdit={canEdit}
                                />
                              )
                            })}
                          </>
                        ) : (
                          <>
                            {assets.map(({ asset, items }, index) => {
                              if (groupedData.length > 0) {
                                const cellTotalCost =
                                  items.length > 0 ? items.reduce((acc, obj) => acc + obj.cost, 0) : 0

                                const itemIds = items.map((n: any) => n.uuid)
                                const productsInCell = allProducts.filter((n) => itemIds.includes(n.uuid))

                                // Check if any product in this cell needs the dot indicator
                                // Dot should show only when:
                                // 1. Product is shared across multiple cells
                                // 2. Manual distribution is used (is_auto = false)
                                // 3. Cost distribution (costBps) totals less than 100% (10000 basis points)
                                const cellNeedsDot = productsInCell.some((product) => {
                                  const productId = product.uuid
                                  const isManualDistribution = product?.license?.is_auto === false

                                  // Only check further if manual distribution is used
                                  if (!isManualDistribution) return false

                                  /* ONYXIA-6975: TODO: need endpoint to show ended products license */
                                  const productInstances = groupedData.map((group) => {
                                    return group.items.map((item) => {
                                      if ((item as any).uuid === productId) {
                                        return { ...item, nist: group.nist, asset: group.asset }
                                      }
                                      return null
                                    })
                                  })
                                  const flattenedInstances = productInstances
                                    .flat()
                                    .filter((n) => !!n) as unknown as SSMResponseProductExtended[]

                                  // Only show dot if product is shared across multiple cells
                                  if (flattenedInstances.length <= 1) return false

                                  // Check if costBps sum is less than 100% (10000 basis points)
                                  const totalCostBps = flattenedInstances.reduce(
                                    (acc, obj) => acc + ((obj as any).costBps || 0),
                                    0
                                  )
                                  return totalCostBps < 10000
                                })
                                return (
                                  <BudgetViewCard
                                    nist={nist as SSMNistPillar}
                                    asset={asset as SSMAssetType}
                                    items={items as unknown as SSMResponseProduct[]}
                                    handleOpenDrawer={handleOpenDrawer}
                                    key={index}
                                    budgetBreakpoints={budgetBreakpoints}
                                    sumNotMatching={cellNeedsDot}
                                    cellTotalCost={cellTotalCost}
                                    canEdit={canEdit}
                                  />
                                )
                              }

                              return null
                            })}
                          </>
                        )}
                      </>
                    </Fragment>
                  )
                })}
              </SSMGrid>
            </ContentContainer>

            <SSMDrawer
              drawerProps={drawerProps}
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              allProducts={allProducts}
              setEmptyStateBanner={setEmptyStateBanner}
              groupedData={groupedData}
              setGroupedData={setGroupedData}
              updateDrawerProps={updateDrawerProps}
              fetchAndGroupData={fetchAndGroupData}
              cell={cell}
              setCell={setCell}
              contractYear={contractYear}
              setConfirmRemoveProps={setConfirmRemoveProps}
              fetchCellDetails={fetchCellDetails}
              fetchingCell={fetchingCell}
            />
          </>
        )}

        {tabValue === 1 && <SSMHistoricalOverview />}
      </Container>
      <ConfirmRemovalDialog
        onClose={handleCloseConfirmRemoveDialog}
        {...confirmRemoveProps}
        handleClick={
          confirmRemoveProps.type === 'global' && confirmRemoveProps.affectedCells
            ? handleRemoveCompletely
            : handleRemoveFromCell
        }
        removing={removing}
      />
    </>
  )
}

export default SSM
